import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

export type SongNavigationItem = {
  id: string;
  title: string;
  href: string;
} | null;

type Props = {
  href: string;
  children: string;
  actions?: ReactNode;
  prevSong?: SongNavigationItem;
  nextSong?: SongNavigationItem;
};

function NavButton({
  song,
  direction,
}: {
  song: SongNavigationItem;
  direction: "prev" | "next";
}) {
  const isPrev = direction === "prev";
  const disabledLabel = "[none]";

  if (!song) {
    return (
      <span
        aria-disabled="true"
        className="inline-flex items-center gap-1 text-sm text-text-muted/50"
      >
        {isPrev && <ChevronLeft className="h-4 w-4" aria-hidden="true" />}
        <span className="max-w-[120px] truncate sm:max-w-[180px]">
          {disabledLabel}
        </span>
        {!isPrev && <ChevronRight className="h-4 w-4" aria-hidden="true" />}
      </span>
    );
  }

  return (
    <Link
      to={song.href}
      className="group inline-flex items-center gap-1 text-sm text-text-secondary transition-colors hover:text-amber"
    >
      {isPrev && (
        <ChevronLeft
          className="h-4 w-4 transition-transform duration-150 group-hover:-translate-x-0.5"
          aria-hidden="true"
        />
      )}
      <span className="max-w-[120px] truncate sm:max-w-[180px]">
        {song.title}
      </span>
      {!isPrev && (
        <ChevronRight
          className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5"
          aria-hidden="true"
        />
      )}
    </Link>
  );
}

export function BackLink({
  href,
  children,
  actions,
  prevSong,
  nextSong,
}: Props) {
  const hasSetlistNav = prevSong !== undefined || nextSong !== undefined;

  return (
    <nav className="mb-8 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Link
          to={href}
          className="group inline-flex items-center gap-2 text-sm text-text-secondary transition-colors hover:text-amber"
        >
          <span className="transition-transform duration-150 group-hover:-translate-x-1">
            &larr;
          </span>
          {children}
        </Link>
        {actions}
      </div>
      {hasSetlistNav && (
        <div className="flex items-center justify-between">
          <NavButton song={prevSong ?? null} direction="prev" />
          <NavButton song={nextSong ?? null} direction="next" />
        </div>
      )}
    </nav>
  );
}
